import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, FileText, MessageSquare, PenTool, BookOpen, DollarSign, BarChart3, Check, Shield, ImageOff, Lock, Sparkles, Stethoscope, FlaskConical, ChevronDown, AlertTriangle, Trash2, X, CheckSquare, Square, Search, Megaphone, Trophy, Pin, Flag, Flame, Send, Coins, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authFetch } from '../utils/authUtils';
import ShopAnalytics from '../components/ShopAnalytics';
import LibraryHealth from '../components/LibraryHealth';
import LibraryHealthPanel from '../components/LibraryHealthPanel';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import CoverAudit from '../components/CoverAudit';
import GrantVaultModal from '../components/GrantVaultModal';
import GrantPremiumModal from '../components/GrantPremiumModal';
import AdminVaultManager from '../components/AdminVaultManager';
import OnboardingAnalytics from '../components/OnboardingAnalytics';
import AdminExperiments from '../components/AdminExperiments';
import BackupStatusWidget from '../components/BackupStatusWidget';
import FeaturedRailManager from '../components/FeaturedRailManager';
import TestArtifactsCleanup from '../components/TestArtifactsCleanup';
import PriceTagEditor from '../components/PriceTagEditor';
import StalePendingCleanup from '../components/StalePendingCleanup';
import AdminAnnouncementComposer from '../components/AdminAnnouncementComposer';
import AdminMessageBroadcastModal from '../components/AdminMessageBroadcastModal';
import AdminMessagesTab from '../components/AdminMessagesTab';
import AdminQuestsTab from '../components/AdminQuestsTab';
import AdminContractsTab from '../components/AdminContractsTab';
import AdminTopSeriesTab from '../components/AdminTopSeriesTab';
import AdminContentReportsTab from '../components/AdminContentReportsTab';
import AdminPlayerAdsTab from '../components/AdminPlayerAdsTab';
import AdminBookClubTab from '../components/AdminBookClubTab';
import AdminTrashTab from '../components/AdminTrashTab';
import AdminSubscriptionTransfersTab from '../components/AdminSubscriptionTransfersTab';
import AdminQuickStats from '../components/AdminQuickStats';
import AdminPaymentsTab from '../components/AdminPaymentsTab';
import TempPasswordModal from '../components/TempPasswordModal';
import GiftStreakModal from '../components/GiftStreakModal';
import RolePill from '../components/RolePill';
import UserProfileDrawer from '../components/UserProfileDrawer';
import AdminUserActivityDialog from '../components/AdminUserActivityDialog';
import TTSStatusBadge from '../components/TTSStatusBadge';
import CoinCentralBank from '../components/CoinCentralBank';
import AmbassadorSlotsPanel from '../components/AmbassadorSlotsPanel';
import AccountMergeCard from '../components/AccountMergeCard';
import UserActionsMenu from '../components/UserActionsMenu';
import { Toaster, toast } from 'react-hot-toast';
import runWithUndo from '../utils/runWithUndo';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    // Allow deep-linking from the TopBar's quick shortcuts (Users / Analytics)
    // via navigate('/admin', { state: { tab: 'analytics' } }). Falls back to
    // the long-standing default of 'users'. Feb 26 2026.
    const t = location?.state?.tab;
    return typeof t === 'string' ? t : 'users';
  });
  // Sync if user lands on /admin already, then clicks the quick shortcut
  // a second time — react-router preserves state across same-route nav so
  // we need a useEffect to re-pick it up.
  useEffect(() => {
    const t = location?.state?.tab;
    if (typeof t === 'string' && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.tab]);
  const [users, setUsers] = useState([]);
  const [writers, setWriters] = useState([]);
  const [content, setContent] = useState([]);
  const [pendingContent, setPendingContent] = useState([]);
  const [loading, setLoading] = useState(true);
  // Multi-select state for the Content Library — Set keeps add/remove O(1).
  const [contentSelectMode, setContentSelectMode] = useState(false);
  const [contentSelectedIds, setContentSelectedIds] = useState(new Set());
  const [contentBulkBusy, setContentBulkBusy] = useState(false);
  const [contentSearch, setContentSearch] = useState('');
  // Pending Content multi-select (bulk approve / reject).
  const [pendingSelectMode, setPendingSelectMode] = useState(false);
  const [pendingSelectedIds, setPendingSelectedIds] = useState(new Set());
  const [pendingBulkBusy, setPendingBulkBusy] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [selectedContent, setSelectedContent] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [activityUserId, setActivityUserId] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null); // {id,email,name,kind:'user'|'writer'}
  const [pendingRevokeVault, setPendingRevokeVault] = useState(null); // {userId, name, email}
  const [autoTagging, setAutoTagging] = useState(false);
  const [untaggedCount, setUntaggedCount] = useState(0);
  const [generatingHooks, setGeneratingHooks] = useState(false);
  const [missingHooksCount, setMissingHooksCount] = useState(0);
  const [autoCovering, setAutoCovering] = useState(false);
  const [missingCoversCount, setMissingCoversCount] = useState(0);
  const [polishing, setPolishing] = useState(false);
  const [healthPanelOpen, setHealthPanelOpen] = useState(false);
  const [grantVaultUser, setGrantVaultUser] = useState(null);
  const [grantPremiumUser, setGrantPremiumUser] = useState(null);
  const [pendingWriters, setPendingWriters] = useState([]);
  const [pendingWritersCount, setPendingWritersCount] = useState(0);
  const [decidingWriterId, setDecidingWriterId] = useState('');
  const [expandedWriterId, setExpandedWriterId] = useState('');
  const [activityCache, setActivityCache] = useState({}); // user_id -> activity payload
  const [loadingActivityFor, setLoadingActivityFor] = useState('');
  const [selectedWriterIds, setSelectedWriterIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [scanningSpam, setScanningSpam] = useState(false);
  const [roleFilter, setRoleFilter] = useState(() => {
    // Deep-link from /admin-panel stat chips: navigate('/admin', {state:{tab:'users', roleFilter:'user'|'writer'|'admin'|'all'}}).
    const rf = location?.state?.roleFilter;
    return typeof rf === 'string' && ['all','user','writer','admin'].includes(rf) ? rf : 'all';
  }); // all | user | writer | admin
  // Re-pick the role filter if user lands on /admin and the state changes
  // (e.g. they tap Readers chip while already on the Users tab).
  useEffect(() => {
    const rf = location?.state?.roleFilter;
    if (typeof rf === 'string' && ['all','user','writer','admin'].includes(rf) && rf !== roleFilter) {
      setRoleFilter(rf);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.roleFilter]);
  const [announcementComposerOpen, setAnnouncementComposerOpen] = useState(false);
  const [messageBroadcastOpen, setMessageBroadcastOpen] = useState(false);
  // Free-text user search — matches name, email, country, role.
  const [userSearch, setUserSearch] = useState('');
  // Gift-streak modal state — null when closed, holds {user_id, name} when open.
  const [giftStreakTarget, setGiftStreakTarget] = useState(null);
  const [giftStreakDays, setGiftStreakDays] = useState(7);
  const [giftStreakReason, setGiftStreakReason] = useState('');
  const [giftStreakBusy, setGiftStreakBusy] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Jun 2 2026 — co-admin role is read-only across the dashboard.
  // We read the role from localStorage (set by AdminLogin) and use it
  // to (a) gate write-side handlers with a friendly toast, (b) hide
  // promote/revoke/delete affordances, and (c) show a banner.
  const viewerRole = typeof window !== 'undefined' ? localStorage.getItem('userRole') : 'admin';
  const selfUserId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
  const isCoadmin = viewerRole === 'coadmin' || viewerRole === 'king';
  // Profile drawer state — null when closed, user_id when open.
  const [profileUserId, setProfileUserId] = useState(null);
  const guardWrite = () => {
    if (isCoadmin) {
      toast.error('Co-admin is read-only — only the admin can make changes.');
      return false;
    }
    return true;
  };
  // Co-admin management state.
  const [coadmins, setCoadmins] = useState({ count: 0, cap: 2, coadmins: [] });
  const [coadminBusyId, setCoadminBusyId] = useState('');

  const fetchPendingWriters = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/api/admin/pending-writers`),
        authFetch(`${BACKEND_URL}/api/admin/pending-writers/count`),
      ]);
      if (listRes.ok) {
        const d = await listRes.json();
        setPendingWriters(d.applications || []);
      }
      if (countRes.ok) {
        const d = await countRes.json();
        setPendingWritersCount(d.count || 0);
      }
    } catch { /* swallow */ }
  }, [BACKEND_URL]);

  const decideWriter = async (userId, action, name) => {
    if (decidingWriterId) return;
    if (action === 'reject' && !window.confirm(`Reject "${name}"'s writer application? They'll remain a regular reader.`)) return;
    setDecidingWriterId(userId);
    const t = toast.loading(action === 'approve' ? 'Approving...' : 'Rejecting...');
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/pending-writers/${userId}/decide`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(d.detail || 'Decision failed', { id: t });
        return;
      }
      toast.success(
        action === 'approve' ? `✅ ${name} is now a scribe.` : `${name} declined.`,
        { id: t },
      );
      fetchPendingWriters();
    } finally {
      setDecidingWriterId('');
    }
  };

  const toggleWriterActivity = async (userId) => {
    // Collapse if already open.
    if (expandedWriterId === userId) {
      setExpandedWriterId('');
      return;
    }
    setExpandedWriterId(userId);
    if (activityCache[userId] || loadingActivityFor === userId) return;
    setLoadingActivityFor(userId);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/pending-writers/${userId}/activity`);
      if (res.ok) {
        const d = await res.json();
        setActivityCache((prev) => ({ ...prev, [userId]: d }));
      } else {
        toast.error('Failed to load activity');
      }
    } finally {
      setLoadingActivityFor('');
    }
  };

  const toggleSelectWriter = (userId) => {
    setSelectedWriterIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const allWriterIdsOnPage = () => pendingWriters.map((w) => w.user_id);

  const toggleSelectAllWriters = () => {
    setSelectedWriterIds((prev) => {
      const allIds = allWriterIdsOnPage();
      const allChecked = allIds.length > 0 && allIds.every((id) => prev.has(id));
      return allChecked ? new Set() : new Set(allIds);
    });
  };

  // Pre-fetch activity for any visible row that doesn't have it cached, then
  // pre-select every spam_risk row. Useful for "purge ghost applications".
  const selectAllSpamRisk = async () => {
    if (scanningSpam) return;
    setScanningSpam(true);
    const t = toast.loading('Scanning applications for spam risk...');
    try {
      const ids = allWriterIdsOnPage();
      const missing = ids.filter((id) => !activityCache[id]);
      const fetched = await Promise.all(
        missing.map(async (id) => {
          try {
            const r = await authFetch(`${BACKEND_URL}/api/admin/pending-writers/${id}/activity`);
            if (r.ok) {
              const d = await r.json();
              return [id, d];
            }
          } catch { /* ignore */ }
          return [id, null];
        }),
      );
      // Merge into the cache.
      setActivityCache((prev) => {
        const next = { ...prev };
        fetched.forEach(([id, d]) => { if (d) next[id] = d; });
        return next;
      });
      // Pick the spam-risk ones (combining what we just fetched + cache).
      const merged = { ...activityCache };
      fetched.forEach(([id, d]) => { if (d) merged[id] = d; });
      const spamIds = ids.filter((id) => merged[id]?.spam_risk);
      setSelectedWriterIds(new Set(spamIds));
      if (spamIds.length === 0) {
        toast.success('No spam-risk applications detected.', { id: t });
      } else {
        toast.success(`Selected ${spamIds.length} spam-risk application${spamIds.length === 1 ? '' : 's'}.`, { id: t });
      }
    } finally {
      setScanningSpam(false);
    }
  };

  const bulkDecide = async (action) => {
    const ids = Array.from(selectedWriterIds);
    if (ids.length === 0 || bulkBusy) return;
    const verb = action === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`Bulk ${verb} ${ids.length} application${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    const t = toast.loading(`Bulk ${verb}ing ${ids.length}...`);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/admin/pending-writers/bulk-decide`, {
        method: 'POST',
        body: JSON.stringify({ action, user_ids: ids }),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(d.detail || 'Bulk decision failed', { id: t });
        return;
      }
      const skippedNote = d.skipped > 0 ? `, ${d.skipped} skipped` : '';
      toast.success(
        action === 'approve'
          ? `✅ Approved ${d.modified}${skippedNote}.`
          : `Rejected ${d.modified}${skippedNote}.`,
        { id: t },
      );
      setSelectedWriterIds(new Set());
      fetchPendingWriters();
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    // Check if admin or co-admin is logged in via localStorage.
    const userRole = localStorage.getItem('userRole');
    const userEmail = localStorage.getItem('userEmail');

    if (!userRole || (userRole !== 'admin' && userRole !== 'coadmin' && userRole !== 'king')) {
      // Not logged in as admin/king
      navigate('/admin-login');
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, BACKEND_URL]);

  const fetchData = async () => {
    try {
      // Fetch users from database
      const usersRes = await authFetch(`${BACKEND_URL}/api/admin/users`);
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      // Fetch library content
      const contentRes = await authFetch(`${BACKEND_URL}/api/library`);
      if (contentRes.ok) {
        const data = await contentRes.json();
        setContent(data.books || []);
      }

      // Fetch pending content
      const pendingRes = await authFetch(`${BACKEND_URL}/api/admin/pending-content`);
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingContent(data.pending_content || []);
      }

      // Co-admin roster (read-accessible to admin + coadmin).
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/coadmin/list`);
        if (r.ok) {
          const d = await r.json();
          setCoadmins({
            count: d.count || 0,
            cap: d.cap || 2,
            remaining: d.remaining ?? Math.max(0, (d.cap || 2) - (d.count || 0)),
            coadmins: d.coadmins || [],
          });
        }
      } catch { /* non-fatal */ }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Co-admin promote / revoke (admin-only writes) ──────────────
  const refreshCoadmins = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/coadmin/list`);
      if (r.ok) {
        const d = await r.json();
        setCoadmins({
          count: d.count || 0,
          cap: d.cap || 2,
          remaining: d.remaining ?? Math.max(0, (d.cap || 2) - (d.count || 0)),
          coadmins: d.coadmins || [],
        });
      }
    } catch { /* non-fatal */ }
  };

  const handlePromoteCoadmin = async (userId, label = '') => {
    if (!guardWrite()) return;
    if (!window.confirm(
      `Promote ${label || 'this user'} to co-admin?\n\n` +
      `Co-admins see all admin analytics but cannot make changes. ` +
      `Cap is ${coadmins.cap} co-admins (${coadmins.count}/${coadmins.cap} used).`,
    )) return;
    setCoadminBusyId(userId);
    const tid = toast.loading('Promoting to co-admin…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/coadmin/promote/${userId}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Promotion failed.', { id: tid });
        return;
      }
      toast.success(d.message || 'Promoted to co-admin.', { id: tid });
      // Reflect role change in local users list + roster.
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? { ...u, role: 'coadmin' } : u)));
      await refreshCoadmins();
    } catch {
      toast.error('Network error promoting co-admin.', { id: tid });
    } finally {
      setCoadminBusyId('');
    }
  };

  const handleRevokeCoadmin = async (userId, label = '') => {
    if (!guardWrite()) return;
    if (!window.confirm(`Revoke co-admin from ${label || 'this user'}?`)) return;
    setCoadminBusyId(userId);
    const tid = toast.loading('Revoking co-admin…');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/coadmin/revoke/${userId}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Revoke failed.', { id: tid });
        return;
      }
      toast.success(d.message || 'Co-admin revoked.', { id: tid });
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? { ...u, role: d.restored_role || 'reader' } : u)));
      await refreshCoadmins();
    } catch {
      toast.error('Network error revoking co-admin.', { id: tid });
    } finally {
      setCoadminBusyId('');
    }
  };

  const handleResetUserPassword = async (userIdOrEmail, userEmail) => {
    // Supports both old call signature (just email) and the new (id, email)
    const userId = userEmail === undefined ? null : userIdOrEmail;
    const displayEmail = userEmail || userIdOrEmail;
    try {
      let response;
      if (userId) {
        // New flow: admin resets a specific user_id and we get the plaintext back
        response = await authFetch(
          `${BACKEND_URL}/api/admin/reset-user-password/${userId}`,
          { method: 'POST' },
        );
      } else {
        // Fallback: email-based path (still supported server-side)
        response = await authFetch(
          `${BACKEND_URL}/api/admin/reset-user-password`,
          { method: 'POST', body: JSON.stringify({ email: displayEmail }) },
        );
      }

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to reset password');
        return;
      }

      // New endpoint returns temp_password; old one returns temporary_password
      const pwd = data.temp_password || data.temporary_password;
      setTempPassword(pwd);
      setResetPasswordUser(displayEmail);
    } catch (error) {
      console.error('Password reset failed:', error);
      alert('Failed to reset password');
    }
  };

  const [passwordCopied, setPasswordCopied] = useState(false);
  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      // Fallback: select the text for manual copy
      const el = document.getElementById('admin-temp-password-value');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const closePasswordModal = () => {
    setResetPasswordUser(null);
    setTempPassword('');
    setPasswordCopied(false);
  };

  // Gift streak — admin-only goodwill gesture. Bumps user_gamification
  // streak_current to the gifted value (won't downgrade an existing higher
  // streak) and writes an audit row to streak_gifts.
  const openGiftStreakModal = (user) => {
    if (isCoadmin) {
      toast.error('Read-only admin — only super-admins can gift streaks.');
      return;
    }
    setGiftStreakTarget({ user_id: user.user_id, name: user.name || user.email });
    setGiftStreakDays(7);
    setGiftStreakReason('');
  };

  const closeGiftStreakModal = () => {
    setGiftStreakTarget(null);
    setGiftStreakBusy(false);
  };

  const submitGiftStreak = async () => {
    if (!giftStreakTarget) return;
    const days = parseInt(giftStreakDays, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error('Days must be between 1 and 365.');
      return;
    }
    if (!giftStreakReason.trim()) {
      toast.error('Please add a short reason — it goes into the audit log.');
      return;
    }
    setGiftStreakBusy(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/api/admin/users/${giftStreakTarget.user_id}/gift-streak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days, reason: giftStreakReason.trim() }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to gift streak');
      }
      const data = await response.json();
      toast.success(`Streak gifted: ${data.previous_streak} → ${data.new_streak} day${data.new_streak !== 1 ? 's' : ''}.`);
      closeGiftStreakModal();
    } catch (e) {
      toast.error(e.message || 'Failed to gift streak');
      setGiftStreakBusy(false);
    }
  };

  const handleGrantOfflineLibrary = async (userId) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/admin/grant-offline-library/${userId}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to grant access');
        return;
      }

      alert('✅ Offline Library access granted successfully!');
      fetchData(); // Refresh user list
    } catch (error) {
      console.error('Grant failed:', error);
      alert('Failed to grant offline library access');
    }
  };

  const handleRevokeOfflineLibrary = async (userId) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/admin/revoke-offline-library/${userId}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.detail || 'Failed to revoke access');
        return;
      }

      alert('🔒 Offline Library access revoked successfully');
      fetchData(); // Refresh user list
    } catch (error) {
      console.error('Revoke failed:', error);
      alert('Failed to revoke offline library access');
    }
  };

  const handleRevokeVault = (userId, userMeta = null) => {
    // Open the styled confirmation dialog instead of a raw window.confirm.
    setPendingRevokeVault({
      userId,
      name: userMeta?.name || userMeta?.email || 'this user',
      email: userMeta?.email || '',
    });
  };

  const confirmRevokeVault = () => {
    if (!pendingRevokeVault) return;
    const { userId } = pendingRevokeVault;
    setPendingRevokeVault(null);
    runWithUndo({
      label: '🔒 Vault revoked',
      delayMs: 10000,
      testIdPrefix: 'revoke-vault',
      action: async () => {
        const response = await authFetch(`${BACKEND_URL}/api/admin/vault/revoke/${userId}`, {
          method: 'POST',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || 'Failed to revoke vault access');
      },
      onCommitted: () => fetchData(),
    });
  };

  // Fetch the count of untagged content rows so the auto-tag button shows progress.
  const fetchUntaggedCount = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/untagged-genres-count`);
      if (r.ok) {
        const d = await r.json();
        setUntaggedCount(d.count || 0);
      }
    } catch {/* noop */}
  }, [BACKEND_URL]);

  const fetchMissingHooksCount = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/missing-hooks-count`);
      if (r.ok) {
        const d = await r.json();
        setMissingHooksCount(d.count || 0);
      }
    } catch {/* noop */}
  }, [BACKEND_URL]);

  const fetchMissingCoversCount = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/missing-covers-count`);
      if (r.ok) {
        const d = await r.json();
        setMissingCoversCount(d.count || 0);
      }
    } catch {/* noop */}
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchUntaggedCount();
    fetchMissingHooksCount();
    fetchMissingCoversCount();
    fetchPendingWriters();
    // Poll every 60 s so the badge wakes up when new applications arrive.
    const id = setInterval(fetchPendingWriters, 60000);
    return () => clearInterval(id);
  }, [fetchUntaggedCount, fetchMissingHooksCount, fetchMissingCoversCount, fetchPendingWriters]);

  const handleAutoTagGenres = async () => {
    if (autoTagging) return;
    if (!window.confirm(
      `Auto-tag missing genres on up to 50 untagged items? Uses AI to classify each one — should take ~30 seconds.`
    )) return;
    setAutoTagging(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/auto-tag-genres`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(d.detail || 'Auto-tag failed');
        return;
      }
      const sample = (d.items || []).slice(0, 5).map((it) => `  · ${it.title} → ${it.genre || '?'}`).join('\n');
      alert(
        `✓ Tagged ${d.updated} item(s). ${d.remaining} still untagged.\n\nSample:\n${sample}`
      );
      await fetchUntaggedCount();
      await fetchData();
    } catch (e) {
      alert('Auto-tag failed: ' + (e.message || 'unknown error'));
    } finally {
      setAutoTagging(false);
    }
  };

  const handleGenerateHooks = async () => {
    if (generatingHooks) return;
    if (!window.confirm(
      `Generate AI hook + reading level for up to 50 items missing them? Takes about a minute.`
    )) return;
    setGeneratingHooks(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/auto-generate-hooks`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(d.detail || 'Hook generation failed');
        return;
      }
      const sample = (d.items || []).slice(0, 3).map((it) => `  · ${it.title}\n    [${it.reading_level || '?'}] ${it.hook || it.error || '?'}`).join('\n\n');
      alert(`✓ Generated hooks for ${d.updated} item(s). ${d.remaining} still missing.\n\nSample:\n${sample}`);
      await fetchMissingHooksCount();
      await fetchData();
    } catch (e) {
      alert('Hook generation failed: ' + (e.message || 'unknown error'));
    } finally {
      setGeneratingHooks(false);
    }
  };

  const handleAutoCover = async () => {
    if (autoCovering) return;
    if (!window.confirm(
      `Generate a gradient placeholder cover for up to 50 items missing one?`,
    )) return;
    setAutoCovering(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/auto-cover`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        alert(d.detail || 'Cover generation failed');
        return;
      }
      const sample = (d.items || []).slice(0, 3).map((it) => `  · [${it.collection}] ${it.title}`).join('\n');
      alert(`✓ Painted covers for ${d.updated} item(s). ${d.remaining} still missing.\n\nSample:\n${sample}`);
      await fetchMissingCoversCount();
      await fetchData();
    } catch (e) {
      alert('Cover generation failed: ' + (e.message || 'unknown error'));
    } finally {
      setAutoCovering(false);
    }
  };

  /**
   * "AI Polish All" — runs Auto-tag genres → Auto-write hooks → Auto-cover
   * sequentially with a single live-updating toast. Each phase is a no-op
   * when its respective backlog is already at 0.
   */
  const handleAIPolish = async () => {
    if (polishing) return;
    if (!window.confirm(
      'Run the full AI polish pipeline?\n\n' +
      '  1. Auto-tag genres\n  2. Auto-write hooks + reading levels\n  3. Auto-paint missing covers\n\n' +
      'Each phase is capped at 50 items per run and may take ~30–60 seconds.',
    )) return;
    setPolishing(true);
    const tid = toast.loading('Starting AI polish…', { duration: 60000, position: 'bottom-right' });
    let summary = { tagged: 0, hooked: 0, covered: 0, errors: [] };
    try {
      // Phase 1 — genres
      toast.loading('Tagging genres…', { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/auto-tag-genres`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.tagged = d.updated || 0;
        else summary.errors.push(`tag: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`tag: ${e.message}`); }

      // Phase 2 — hooks
      toast.loading(`Tagged ${summary.tagged}. Writing hooks…`, { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/auto-generate-hooks`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.hooked = d.updated || 0;
        else summary.errors.push(`hook: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`hook: ${e.message}`); }

      // Phase 3 — covers
      toast.loading(`Hooks ${summary.hooked}. Painting covers…`, { id: tid });
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/auto-cover`, { method: 'POST' });
        const d = await r.json().catch(() => ({}));
        if (r.ok) summary.covered = d.updated || 0;
        else summary.errors.push(`cover: ${d.detail || r.status}`);
      } catch (e) { summary.errors.push(`cover: ${e.message}`); }

      const fmt = `Polished ✦ ${summary.tagged} tags · ${summary.hooked} hooks · ${summary.covered} covers`;
      if (summary.errors.length) {
        toast.error(`${fmt}\n${summary.errors.join('; ')}`, { id: tid, duration: 8000 });
      } else {
        toast.success(fmt, { id: tid, duration: 5000 });
      }
      // Refresh badges + data
      await Promise.all([
        fetchUntaggedCount(),
        fetchMissingHooksCount(),
        fetchMissingCoversCount(),
        fetchData(),
      ]);
    } finally {
      setPolishing(false);
    }
  };

  const handleDeleteUser = (userId, userEmail, userName) => {
    setPendingDeleteUser({
      id: userId,
      email: userEmail,
      name: userName,
      kind: 'user',
    });
  };

  // ── Content Library multi-select helpers ─────────────────────────────
  // Toggle one item in/out of selection. We mutate via a fresh Set so
  // referential change triggers a re-render.
  const toggleContentSelected = (id) => {
    setContentSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter the content list by title/author/type. Memoised so typing
  // stays snappy on long lists.
  const visibleContent = useMemo(() => {
    const q = contentSearch.trim().toLowerCase();
    if (!q) return content;
    return content.filter((c) =>
      (c.title || '').toLowerCase().includes(q) ||
      (c.author || '').toLowerCase().includes(q) ||
      (c.type || '').toLowerCase().includes(q)
    );
  }, [content, contentSearch]);

  const allContentSelected =
    visibleContent.length > 0 && visibleContent.every((c) => contentSelectedIds.has(c.book_id));

  const exitContentSelectMode = () => {
    setContentSelectMode(false);
    setContentSelectedIds(new Set());
  };

  const runContentBulkDelete = async () => {
    if (contentBulkBusy) return;
    const ids = Array.from(contentSelectedIds);
    if (!ids.length) return;
    if (!window.confirm(
      `Delete ${ids.length} item${ids.length === 1 ? '' : 's'} permanently? This cannot be undone.`
    )) return;
    setContentBulkBusy(true);
    const tid = toast.loading(`Deleting ${ids.length} item${ids.length === 1 ? '' : 's'}…`);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/library/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_ids: ids }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const ok = data.deleted_count || 0;
      const fail = data.failed_count || 0;
      if (fail === 0) {
        toast.success(`✓ ${ok} item${ok === 1 ? '' : 's'} deleted`, { id: tid });
      } else {
        toast.error(`Done — ${ok} deleted, ${fail} failed`, { id: tid });
      }
      exitContentSelectMode();
      fetchData();
    } catch (e) {
      toast.error(`Bulk delete failed: ${e?.message || 'unknown'}`, { id: tid });
    } finally {
      setContentBulkBusy(false);
    }
  };

  // ── Pending Content multi-select helpers ─────────────────────────────
  const togglePendingSelected = (id) => {
    setPendingSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visiblePending = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return pendingContent;
    return pendingContent.filter((c) =>
      (c.title || '').toLowerCase().includes(q) ||
      (c.content_type || '').toLowerCase().includes(q) ||
      (c.user_id || '').toLowerCase().includes(q)
    );
  }, [pendingContent, pendingSearch]);

  const allPendingSelected =
    visiblePending.length > 0 && visiblePending.every((c) => pendingSelectedIds.has(c.content_id));

  const exitPendingSelectMode = () => {
    setPendingSelectMode(false);
    setPendingSelectedIds(new Set());
  };

  const runPendingBulkAction = async (action) => {
    if (pendingBulkBusy) return;
    const ids = Array.from(pendingSelectedIds);
    if (!ids.length) return;
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} ${ids.length} item${ids.length === 1 ? '' : 's'}?`)) return;
    setPendingBulkBusy(true);
    const tid = toast.loading(`${verb}ing ${ids.length} item${ids.length === 1 ? '' : 's'}…`);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/content/bulk-moderate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            content_ids: ids,
            message:
              actionMessage ||
              (action === 'approve'
                ? 'Content approved'
                : 'Content does not meet our standards'),
          }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const ok = data.ok_count || 0;
      const fail = data.failed_count || 0;
      if (fail === 0) {
        toast.success(`✓ ${ok} ${action}d`, { id: tid });
      } else {
        toast.error(`Done — ${ok} ${action}d, ${fail} failed`, { id: tid });
      }
      exitPendingSelectMode();
      fetchData();
    } catch (e) {
      toast.error(`Bulk ${action} failed: ${e?.message || 'unknown'}`, { id: tid });
    } finally {
      setPendingBulkBusy(false);
    }
  };

  const handleDeleteWriter = (writerId, writerEmail, writerName) => {
    setPendingDeleteUser({
      id: writerId,
      email: writerEmail,
      name: writerName,
      kind: 'writer',
    });
  };

  const confirmDeleteUserOrWriter = async () => {
    if (!pendingDeleteUser) return;
    const { id, name, kind } = pendingDeleteUser;
    const endpoint =
      kind === 'writer'
        ? `${BACKEND_URL}/api/admin/writers/${id}`
        : `${BACKEND_URL}/api/admin/users/${id}`;
    // Close the confirm modal immediately, then run the actual delete after a
    // 3-second undo window so admins can recover from accidental confirms.
    setPendingDeleteUser(null);
    runWithUndo({
      label: `🗑️ ${kind === 'writer' ? 'Scribe' : 'User'} "${name}" deleted`,
      delayMs: 3000,
      testIdPrefix: 'delete-user',
      action: async () => {
        const res = await authFetch(endpoint, { method: 'DELETE' });
        if (!res.ok) {
          const error = await res.json().catch(() => ({}));
          throw new Error(error.detail || `Failed to delete ${kind}`);
        }
      },
      onCommitted: () => fetchData(),
    });
  };


  const handleApprove = async (contentId) => {
    const message = actionMessage || 'Content approved';
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/content/${contentId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message })
      });
      
      if (res.ok) {
        alert('Content approved successfully!');
        setActionMessage('');
        setSelectedContent(null);
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Approval failed:', error);
      alert('Failed to approve content');
    }
  };

  const handleReject = async (contentId) => {
    const message = actionMessage || 'Content does not meet our standards';
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/content/${contentId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message })
      });
      
      if (res.ok) {
        alert('Content rejected');
        setActionMessage('');
        setSelectedContent(null);
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Rejection failed:', error);
      alert('Failed to reject content');
    }
  };

  return (
    <>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <main
        className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8"
        data-testid="admin-dashboard"
      >
        {isCoadmin && (
          <div
            className="mb-4 rounded-xl border border-amber-400/40 bg-amber-500/10 text-amber-100 px-4 py-3 flex items-center gap-3"
            data-testid="coadmin-readonly-banner"
          >
            <Shield className="w-5 h-5 text-amber-300 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold">Co-admin · read-only access</p>
              <p className="text-xs text-amber-200/80">
                You can view all analytics. Write actions (promote, delete, grant, approve, reject) are reserved for the admin.
              </p>
            </div>
          </div>
        )}
        {/* Admin header strip — "Administrator" label + Reader View jump */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center shadow-lg shadow-red-900/30">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                Admin Dashboard
              </h1>
              <p className="text-xs text-red-300/90 uppercase tracking-widest font-semibold">
                Administrator
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TTSStatusBadge />
            <Button
              onClick={() => setMessageBroadcastOpen(true)}
              variant="outline"
              size="sm"
              className="bg-transparent border-indigo-600 text-indigo-300 hover:bg-indigo-600 hover:text-white"
              data-testid="admin-send-to-all-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to all
            </Button>
            <Button
              onClick={() => setAnnouncementComposerOpen(true)}
              variant="outline"
              size="sm"
              className="bg-transparent border-fuchsia-600 text-fuchsia-300 hover:bg-fuchsia-600 hover:text-white"
              data-testid="admin-broadcast-btn"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Broadcast
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              size="sm"
              className="bg-transparent border-indigo-600 text-indigo-300 hover:bg-indigo-600 hover:text-white"
              data-testid="admin-reader-view-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Reader View
            </Button>
          </div>
        </div>

        {/* Announcement composer modal (admins only) */}
        <AdminAnnouncementComposer
          open={announcementComposerOpen}
          onClose={() => setAnnouncementComposerOpen(false)}
        />

        {/* Messaging broadcast modal — sends an inbox-delivered DM to a
            target audience (Users / Writers / Everyone). */}
        <AdminMessageBroadcastModal
          open={messageBroadcastOpen}
          onClose={() => setMessageBroadcastOpen(false)}
        />

        {/* Backup status — shows the latest Mongo→Storage snapshot and lets
            admins fire one on demand. Lives at the top so DB durability is
            the first thing an admin sees on login. */}
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <BackupStatusWidget />
          <FeaturedRailManager />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Tab Navigation - Scrollable on Mobile */}
          <div className="mb-6 overflow-x-auto">
            <TabsList className="bg-slate-800/50 border border-slate-700 inline-flex md:flex w-full md:w-auto min-w-full">
              <TabsTrigger value="users" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                <Users className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Users</span>
                <span className="sm:hidden">Users</span>
              </TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Retain Pending ({pendingContent.length})</span>
                <span className="sm:hidden">Retain</span>
              </TabsTrigger>
              <TabsTrigger value="content" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="writers" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4 relative" data-testid="admin-writers-tab">
                <PenTool className="w-4 h-4 mr-1 sm:mr-2" />
                Writers
                {pendingWritersCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 sm:-right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold flex items-center justify-center shadow-lg shadow-amber-500/40 animate-pulse"
                    data-testid="pending-writers-badge"
                    title={`${pendingWritersCount} pending writer application${pendingWritersCount === 1 ? '' : 's'}`}
                  >
                    {pendingWritersCount > 9 ? '9+' : pendingWritersCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="payments" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4">
                <DollarSign className="w-4 h-4 mr-1 sm:mr-2" />
                Payments
              </TabsTrigger>
              <TabsTrigger value="central-bank" className="data-[state=active]:bg-amber-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-central-bank-tab">
                <Coins className="w-4 h-4 mr-1 sm:mr-2" />
                Central Bank
              </TabsTrigger>
              <TabsTrigger value="shop" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-shop-tab">
                <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
                Shop
              </TabsTrigger>
              <TabsTrigger value="audit" className="data-[state=active]:bg-indigo-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-audit-tab">
                <ImageOff className="w-4 h-4 mr-1 sm:mr-2" />
                Audit
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-cyan-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-analytics-tab">
                <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="vault" className="data-[state=active]:bg-fuchsia-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-vault-tab">
                <Lock className="w-4 h-4 mr-1 sm:mr-2" />
                Vault
              </TabsTrigger>
              <TabsTrigger value="experiments" className="data-[state=active]:bg-fuchsia-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-experiments-tab">
                <FlaskConical className="w-4 h-4 mr-1 sm:mr-2" />
                Experiments
              </TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-pink-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-messages-tab">
                <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
                Messages
              </TabsTrigger>
              <TabsTrigger value="quests" className="data-[state=active]:bg-amber-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-quests-tab">
                <Trophy className="w-4 h-4 mr-1 sm:mr-2" />
                Quests
              </TabsTrigger>
              <TabsTrigger value="contracts" className="data-[state=active]:bg-amber-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-contracts-tab">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                Contracts
              </TabsTrigger>
              <TabsTrigger value="top-series" className="data-[state=active]:bg-amber-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-top-series-tab-trigger">
                <Pin className="w-4 h-4 mr-1 sm:mr-2" />
                Top Series
              </TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-rose-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-reports-tab-trigger">
                <Flag className="w-4 h-4 mr-1 sm:mr-2" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="player-ads" className="data-[state=active]:bg-amber-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-player-ads-tab-trigger">
                <Megaphone className="w-4 h-4 mr-1 sm:mr-2" />
                Player Ads
              </TabsTrigger>
              <TabsTrigger value="book-club" className="data-[state=active]:bg-fuchsia-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-book-club-tab-trigger">
                <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
                Book Club
              </TabsTrigger>
              <TabsTrigger value="trash" className="data-[state=active]:bg-rose-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-trash-tab-trigger">
                <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
                Trash
              </TabsTrigger>
              <TabsTrigger value="sub-transfers" className="data-[state=active]:bg-emerald-600 flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4" data-testid="admin-sub-transfers-tab-trigger">
                🇳🇬 Transfers
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* Onboarding funnel — surfaces first-week conversion so admins
                can spot where new users drop off (install vs notifications
                vs first book). */}
            <div className="mb-6">
              <OnboardingAnalytics />
            </div>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex-col items-stretch space-y-3">
                <div className="flex flex-row items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-white">User Management</CardTitle>
                  {/* Role filter pills — makes scribes discoverable in a long
                      list instead of hiding them behind a single purple pill. */}
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid="admin-user-role-filter">
                    {[
                      { key: 'all',    label: 'All',     colour: 'bg-slate-700 text-white' },
                      { key: 'user',   label: 'Readers', colour: 'bg-blue-600/80 text-white' },
                      { key: 'writer', label: 'Scribes', colour: 'bg-purple-600/90 text-white' },
                      { key: 'admin',  label: 'Admins',  colour: 'bg-red-600/80 text-white' },
                    ].map((opt) => {
                      const count = opt.key === 'all'
                        ? users.length
                        : users.filter((u) => (u.role || 'user') === opt.key).length;
                      const active = roleFilter === opt.key;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => setRoleFilter(opt.key)}
                          data-testid={`admin-user-role-filter-${opt.key}`}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                            active
                              ? `${opt.colour} border-transparent`
                              : 'bg-slate-800 text-gray-300 border-slate-600 hover:bg-slate-700'
                          }`}
                        >
                          {opt.label}
                          <span
                            className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                              active ? 'bg-white/25 text-white' : 'bg-slate-700 text-gray-300'
                            }`}
                            data-testid={`admin-user-role-count-${opt.key}`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Free-text search — matches name, email, country, role.
                    Live filters BOTH desktop and mobile renderings. */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="search"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users by name, email, country or role…"
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-900/70 border border-slate-700 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-indigo-500"
                    data-testid="admin-user-search-input"
                  />
                  {userSearch && (
                    <button
                      type="button"
                      onClick={() => setUserSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 text-[11px] font-bold"
                      data-testid="admin-user-search-clear"
                    >
                      CLEAR
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-gray-400">Loading users...</p>
                ) : users.length > 0 ? (
                  (() => {
                    // Filter once, reuse twice (desktop table + mobile cards).
                    const q = userSearch.trim().toLowerCase();
                    const visibleUsers = users.filter((u) => {
                      if (roleFilter !== 'all' && (u.role || 'user') !== roleFilter) return false;
                      if (!q) return true;
                      return (
                        (u.name || '').toLowerCase().includes(q)
                        || (u.email || '').toLowerCase().includes(q)
                        || (u.country || '').toLowerCase().includes(q)
                        || (u.role || '').toLowerCase().includes(q)
                        || (u.user_id || '').toLowerCase().includes(q)
                      );
                    }).sort((a, b) => {
                      // Feb 2026 user spec: profile list is alphabetical
                      // by display name (falls back to email so anonymous
                      // rows still sort deterministically).
                      const an = ((a.name || a.email || '')).toString();
                      const bn = ((b.name || b.email || '')).toString();
                      return an.localeCompare(bn, 'en', { sensitivity: 'base' });
                    });
                    return (
                  <>
                    {/* Empty-state for filtered view so an accidentally-empty
                        "Scribes" or "Admins" tab isn't a silent dead-end. */}
                    {visibleUsers.length === 0 && (
                      <p
                        className="text-gray-400 text-sm py-6 text-center"
                        data-testid="admin-user-role-empty"
                      >
                        {q
                          ? <>No users match "<span className="text-white">{userSearch}</span>".</>
                          : <>No {roleFilter === 'all' ? 'users' : `${roleFilter}s`} match this filter.</>}
                      </p>
                    )}
                    {/* Desktop Table (hidden on mobile) */}
                    <div className="hidden md:block space-y-3">
                      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-sm font-semibold text-gray-400 border-b border-slate-700">
                        <div className="col-span-2">User</div>
                        <div className="col-span-2">Email</div>
                        <div className="col-span-1">Role</div>
                        <div className="col-span-2">Password</div>
                        <div className="col-span-1">Offline Lib</div>
                        <div className="col-span-4">Actions</div>
                      </div>
                      {visibleUsers
                        .map((user) => (
                        <div key={user.user_id} className="grid grid-cols-12 gap-3 items-center p-4 bg-slate-700/30 rounded-lg" data-testid={`admin-user-row-${user.user_id}`}>
                          <button
                            type="button"
                            onClick={() => setActivityUserId(user.user_id)}
                            className="col-span-2 flex items-center gap-2 text-left hover:bg-slate-600/30 rounded-md p-1 -m-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                            data-testid={`open-activity-${user.user_id}-desktop`}
                            title="View activity (1/7/30 day analytics)"
                          >
                            {user.picture ? (
                              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                                <Users className="w-4 h-4 text-white" />
                              </div>
                            )}
                            <p className="text-white text-sm font-medium truncate underline-offset-2 hover:underline">{user.name}</p>
                          </button>
                          <div className="col-span-2">
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                          </div>
                          <div className="col-span-1">
                            <RolePill
                              user={user}
                              viewerRole={viewerRole}
                              selfUserId={selfUserId}
                              onChange={(next) => setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? { ...u, role: next } : u)))}
                              testIdSuffix="-desktop"
                            />
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-gray-500">
                              {user.password_hash ? '•••• hashed' : 'OAuth'}
                            </p>
                          </div>
                          <div className="col-span-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              user.offline_library_granted ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'
                            }`}>
                              {user.offline_library_granted ? '✓' : '✗'}
                            </span>
                          </div>
                          <div className="col-span-4">
                            <div className="flex gap-2 flex-wrap items-center">
                              <UserActionsMenu user={user} onChanged={fetchData} />
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-white border-slate-600 text-xs px-2 py-1"
                                onClick={() => handleResetUserPassword(user.user_id, user.email)}
                                data-testid={`admin-reset-pwd-btn-${user.user_id}`}
                              >
                                Reset Password
                              </Button>
                              {!isCoadmin && user.role !== 'admin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-amber-300 border-amber-500/60 hover:bg-amber-600 hover:text-white text-xs px-2 py-1"
                                  onClick={() => openGiftStreakModal(user)}
                                  title="Gift this user a streak"
                                  data-testid={`admin-gift-streak-btn-${user.user_id}`}
                                >
                                  <Flame className="w-3 h-3 mr-1" /> Gift Streak
                                </Button>
                              )}
                              {!user.offline_library_granted && user.role !== 'admin' ? (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-green-400 border-green-600 hover:bg-green-600 hover:text-white text-xs px-2 py-1"
                                  onClick={() => handleGrantOfflineLibrary(user.user_id)}
                              >
                                Grant Offline
                              </Button>
                            ) : user.role !== 'admin' ? (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-400 border-red-600 hover:bg-red-600 hover:text-white text-xs px-2 py-1"
                                onClick={() => handleRevokeOfflineLibrary(user.user_id)}
                              >
                                Revoke Offline
                              </Button>
                            ) : null}
                            {user.role !== 'admin' && !user.vault_access_granted && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-fuchsia-300 border-fuchsia-500/60 hover:bg-fuchsia-600 hover:text-white text-xs px-2 py-1"
                                onClick={() => setGrantVaultUser(user)}
                                data-testid={`admin-grant-vault-btn-${user.user_id}`}
                              >
                                <Lock className="w-3 h-3 mr-1" /> Grant Vault
                              </Button>
                            )}
                            {user.role !== 'admin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-300 border-amber-500/60 hover:bg-amber-500 hover:text-slate-950 text-xs px-2 py-1"
                                onClick={() => setGrantPremiumUser(user)}
                                data-testid={`admin-grant-premium-btn-${user.user_id}`}
                              >
                                <Crown className="w-3 h-3 mr-1" />
                                {user.subscription_tier && user.subscription_tier !== 'free' ? 'Extend Premium' : 'Grant Premium'}
                              </Button>
                            )}
                            {user.role !== 'admin' && user.vault_access_granted && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-orange-400 border-orange-600 hover:bg-orange-600 hover:text-white text-xs px-2 py-1"
                                onClick={() => handleRevokeVault(user.user_id, user)}
                                data-testid={`admin-revoke-vault-btn-${user.user_id}`}
                              >
                                Revoke Vault
                              </Button>
                            )}
                            {!isCoadmin && user.role !== 'admin' && user.role !== 'coadmin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={coadmins.remaining <= 0 || coadminBusyId === user.user_id}
                                title={coadmins.remaining <= 0
                                  ? `Co-admin cap reached (${coadmins.count}/${coadmins.cap})`
                                  : 'Assign read-only co-admin access'}
                                className="text-cyan-300 border-cyan-500/60 hover:bg-cyan-600 hover:text-white text-xs px-2 py-1 disabled:opacity-50"
                                onClick={() => handlePromoteCoadmin(user.user_id, user.name || user.email)}
                                data-testid={`admin-promote-coadmin-btn-${user.user_id}`}
                              >
                                <Shield className="w-3 h-3 mr-1" /> Make Co-admin
                              </Button>
                            )}
                            {!isCoadmin && user.role === 'coadmin' && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={coadminBusyId === user.user_id}
                                className="text-orange-400 border-orange-600 hover:bg-orange-600 hover:text-white text-xs px-2 py-1 disabled:opacity-50"
                                onClick={() => handleRevokeCoadmin(user.user_id, user.name || user.email)}
                                data-testid={`admin-revoke-coadmin-btn-${user.user_id}`}
                              >
                                <Shield className="w-3 h-3 mr-1" /> Revoke Co-admin
                              </Button>
                            )}
                            {!isCoadmin && user.role !== 'admin' && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-500 border-red-700 hover:bg-red-700 hover:text-white text-xs px-2 py-1"
                                onClick={() => handleDeleteUser(user.user_id, user.email, user.name)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>

                    {/* Mobile Cards (visible on mobile only) */}
                    <div className="md:hidden space-y-4">
                      {visibleUsers
                        .map((user) => (
                        <Card key={user.user_id} className="bg-slate-700/30 border-slate-600">
                          <CardContent className="p-4 space-y-3">
                            {/* User Info — tap opens Activity analytics (Feb 2026). */}
                            <button
                              type="button"
                              onClick={() => setActivityUserId(user.user_id)}
                              className="w-full flex items-start gap-3 text-left rounded-md hover:bg-slate-600/30 p-1 -m-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                              data-testid={`open-activity-${user.user_id}-mobile`}
                              title="View activity (1/7/30 day analytics)"
                            >
                              {user.picture ? (
                                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full shrink-0" />
                              ) : (
                                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center shrink-0">
                                  <Users className="w-6 h-6 text-white" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-white font-semibold underline-offset-2 hover:underline">{user.name}</h3>
                                <p className="text-xs text-gray-400 truncate">{user.email}</p>
                              </div>
                            </button>
                            <div className="flex items-center gap-2 mt-1">
                                  <RolePill
                                    user={user}
                                    viewerRole={viewerRole}
                                    selfUserId={selfUserId}
                                    onChange={(next) => setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? { ...u, role: next } : u)))}
                                    testIdSuffix="-mobile"
                                  />
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    user.offline_library_granted ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'
                                  }`}>
                                    {user.offline_library_granted ? '✓ Offline' : 'No Offline'}
                                  </span>
                                  {user.vault_access_granted && (
                                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-fuchsia-600/20 text-fuchsia-300 border border-fuchsia-500/40 flex items-center gap-1">
                                      <Lock className="w-3 h-3" /> Vault
                                    </span>
                                  )}
                                  {user.id_verified && (
                                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-600/20 text-blue-300 border border-blue-500/40 flex items-center gap-1" data-testid={`admin-id-verified-${user.user_id}`}>
                                      <Check className="w-3 h-3" /> ID Verified
                                    </span>
                                  )}
                                </div>

                            {/* Password status (hash intentionally not displayed) */}
                            <div className="text-xs">
                              <span className="text-gray-500">Password: </span>
                              <span className="text-gray-400">
                                {user.password_hash ? '•••••••• (hashed)' : 'OAuth / no local password'}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-2">
                                <UserActionsMenu user={user} onChanged={fetchData} />
                                <span className="text-xs text-gray-500">More actions</span>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="w-full text-white border-slate-600"
                                onClick={() => handleResetUserPassword(user.user_id, user.email)}
                                data-testid={`admin-reset-pwd-btn-mobile-${user.user_id}`}
                              >
                                Reset Password
                              </Button>
                              {!isCoadmin && user.role !== 'admin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-amber-300 border-amber-500/60 hover:bg-amber-600 hover:text-white"
                                  onClick={() => openGiftStreakModal(user)}
                                  data-testid={`admin-gift-streak-btn-mobile-${user.user_id}`}
                                >
                                  <Flame className="w-3 h-3 mr-1" /> Gift Streak
                                </Button>
                              )}
                              {!user.offline_library_granted && user.role !== 'admin' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full text-green-400 border-green-600 hover:bg-green-600 hover:text-white"
                                  onClick={() => handleGrantOfflineLibrary(user.user_id)}
                                >
                                  Grant Offline Access
                                </Button>
                              )}
                              {user.offline_library_granted && user.role !== 'admin' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full text-orange-400 border-orange-600 hover:bg-orange-600 hover:text-white"
                                  onClick={() => handleRevokeOfflineLibrary(user.user_id)}
                                >
                                  Revoke Offline Access
                                </Button>
                              )}
                              {!user.vault_access_granted && user.role !== 'admin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-fuchsia-300 border-fuchsia-500/60 hover:bg-fuchsia-600 hover:text-white"
                                  onClick={() => setGrantVaultUser(user)}
                                  data-testid={`admin-grant-vault-mobile-${user.user_id}`}
                                >
                                  <Lock className="w-3 h-3 mr-1" /> Grant Vault
                                </Button>
                              )}
                              {user.vault_access_granted && user.role !== 'admin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-orange-400 border-orange-600 hover:bg-orange-600 hover:text-white"
                                  onClick={() => handleRevokeVault(user.user_id, user)}
                                  data-testid={`admin-revoke-vault-mobile-${user.user_id}`}
                                >
                                  Revoke Vault Access
                                </Button>
                              )}
                              {!isCoadmin && user.role !== 'admin' && user.role !== 'coadmin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={coadmins.remaining <= 0 || coadminBusyId === user.user_id}
                                  title={coadmins.remaining <= 0
                                    ? `Co-admin cap reached (${coadmins.count}/${coadmins.cap})`
                                    : 'Assign read-only co-admin access'}
                                  className="w-full text-cyan-300 border-cyan-500/60 hover:bg-cyan-600 hover:text-white disabled:opacity-50"
                                  onClick={() => handlePromoteCoadmin(user.user_id, user.name || user.email)}
                                  data-testid={`admin-promote-coadmin-mobile-${user.user_id}`}
                                >
                                  <Shield className="w-3 h-3 mr-1" /> Make Co-admin
                                </Button>
                              )}
                              {!isCoadmin && user.role === 'coadmin' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={coadminBusyId === user.user_id}
                                  className="w-full text-orange-400 border-orange-600 hover:bg-orange-600 hover:text-white disabled:opacity-50"
                                  onClick={() => handleRevokeCoadmin(user.user_id, user.name || user.email)}
                                  data-testid={`admin-revoke-coadmin-mobile-${user.user_id}`}
                                >
                                  <Shield className="w-3 h-3 mr-1" /> Revoke Co-admin
                                </Button>
                              )}
                              {!isCoadmin && user.role !== 'admin' && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="w-full text-red-500 border-red-700 hover:bg-red-700 hover:text-white"
                                  onClick={() => handleDeleteUser(user.user_id, user.email, user.name)}
                                >
                                  Delete User
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                    );
                  })()
                ) : (
                  <p className="text-gray-400 text-center py-8">No users found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span>Scribe Uploads — Retain Pending</span>
                  <Button
                    onClick={() =>
                      pendingSelectMode ? exitPendingSelectMode() : setPendingSelectMode(true)
                    }
                    variant="outline"
                    className={
                      pendingSelectMode
                        ? 'bg-rose-600 hover:bg-rose-500 text-white border-rose-500'
                        : 'border-emerald-500 text-emerald-300 hover:bg-emerald-500/20'
                    }
                    data-testid="admin-pending-bulk-toggle"
                    title="Toggle multi-select to bulk Retain / Discard"
                  >
                    {pendingSelectMode ? <X className="w-4 h-4 mr-2" /> : <CheckSquare className="w-4 h-4 mr-2" />}
                    {pendingSelectMode ? 'Cancel select' : 'Multi-select'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search bar — narrow the queue then bulk-act on the result. */}
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-slate-900/40 border border-slate-700 rounded-lg">
                  <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="search"
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                    placeholder="Search by title, type, or uploader…"
                    className="flex-1 bg-transparent text-white text-sm placeholder-slate-500 focus:outline-none"
                    data-testid="admin-pending-search"
                  />
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold tabular-nums">
                    {visiblePending.length}/{pendingContent.length}
                  </span>
                </div>

                {/* Bulk action bar — pending mode shows BOTH approve and reject. */}
                {pendingSelectMode && (
                  <div
                    className="mb-3 p-3 bg-emerald-900/15 border border-emerald-500/40 rounded-lg flex flex-wrap items-center gap-2 justify-between"
                    data-testid="admin-pending-bulk-bar"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <button
                        type="button"
                        onClick={() =>
                          allPendingSelected
                            ? setPendingSelectedIds(new Set())
                            : setPendingSelectedIds(new Set(visiblePending.map((c) => c.content_id)))
                        }
                        className="inline-flex items-center gap-1.5 text-emerald-200 hover:text-white"
                        data-testid="admin-pending-bulk-select-all"
                      >
                        {allPendingSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        <span>{allPendingSelected ? 'Deselect all' : 'Select all visible'}</span>
                      </button>
                      <span className="text-slate-500">·</span>
                      <span className="text-white font-bold tabular-nums">
                        {pendingSelectedIds.size} selected
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => runPendingBulkAction('approve')}
                        disabled={!pendingSelectedIds.size || pendingBulkBusy}
                        size="sm"
                        className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-bold"
                        data-testid="admin-pending-bulk-approve"
                      >
                        ✓ Retain {pendingSelectedIds.size || ''}
                      </Button>
                      <Button
                        onClick={() => runPendingBulkAction('reject')}
                        disabled={!pendingSelectedIds.size || pendingBulkBusy}
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold"
                        data-testid="admin-pending-bulk-reject"
                      >
                        ✗ Discard {pendingSelectedIds.size || ''}
                      </Button>
                    </div>
                  </div>
                )}

                {loading ? (
                  <p className="text-gray-400">Loading pending content...</p>
                ) : visiblePending.length > 0 ? (
                  <div className="space-y-4">
                    {visiblePending.map((item) => {
                      const isSelected = pendingSelectedIds.has(item.content_id);
                      return (
                      <div
                        key={item.content_id}
                        onClick={pendingSelectMode ? () => togglePendingSelected(item.content_id) : undefined}
                        className={`p-4 rounded-lg border transition-colors ${
                          pendingSelectMode ? 'cursor-pointer ' : ''
                        }${isSelected
                          ? 'bg-emerald-600/20 border-emerald-400/60 ring-2 ring-emerald-400/40'
                          : 'bg-slate-700/30 border-slate-600'}`}
                        data-testid={`admin-pending-row-${item.content_id}`}
                      >
                        <div className="flex items-start justify-between mb-3 gap-3">
                          {/* Checkbox indicator only renders in select mode. */}
                          {pendingSelectMode && (
                            <span className="flex-shrink-0 mt-1">
                              {isSelected
                                ? <CheckSquare className="w-5 h-5 text-emerald-300" />
                                : <Square className="w-5 h-5 text-slate-400" />}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                            <p className="text-sm text-gray-400">Type: {item.content_type} | Format: {item.file_format}</p>
                            <p className="text-sm text-gray-500">Uploaded by: {item.user_id}</p>
                            {item.word_count > 0 && (
                              <p className="text-sm text-gray-500">{item.word_count} words</p>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Price:</span>
                              <PriceTagEditor
                                contentId={item.content_id}
                                value={item.price_coins}
                                onSaved={(next) =>
                                  setPendingContent((prev) =>
                                    prev.map((p) =>
                                      p.content_id === item.content_id
                                        ? { ...p, price_coins: next }
                                        : p
                                    )
                                  )
                                }
                                testIdPrefix="admin-pending-price"
                              />
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded-full">
                            Pending
                          </span>
                        </div>

                        {selectedContent === item.content_id ? (
                          <div className="mt-4 space-y-3">
                            <textarea
                              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm"
                              placeholder="Message to writer (optional)"
                              value={actionMessage}
                              onChange={(e) => setActionMessage(e.target.value)}
                              rows="2"
                            />
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleApprove(item.content_id)}
                                className="bg-green-600 hover:bg-green-700 flex-1"
                              >
                                ✓ Retain
                              </Button>
                              <Button
                                onClick={() => handleReject(item.content_id)}
                                className="bg-red-600 hover:bg-red-700 flex-1"
                              >
                                ✗ Discard
                              </Button>
                              <Button
                                onClick={() => { setSelectedContent(null); setActionMessage(''); }}
                                variant="outline"
                                className="border-slate-600 text-white"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          !pendingSelectMode && (
                            <Button
                              onClick={() => setSelectedContent(item.content_id)}
                              className="bg-indigo-600 hover:bg-indigo-700 mt-3"
                            >
                              Review
                            </Button>
                          )
                        )}
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-8" data-testid="admin-pending-empty">
                    {pendingSearch
                      ? `No pending items match "${pendingSearch}".`
                      : 'No pending approvals'}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <TestArtifactsCleanup onCleaned={() => fetchData?.()} />
            <StalePendingCleanup onCleaned={() => fetchData?.()} />
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Content Library</span>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={handleAIPolish}
                      disabled={polishing}
                      className="bg-gradient-to-r from-fuchsia-600 via-violet-600 to-cyan-600 hover:from-fuchsia-500 hover:via-violet-500 hover:to-cyan-500 text-white border-0 shadow-lg shadow-violet-900/40"
                      data-testid="admin-ai-polish-all-btn"
                      title="Run Auto-tag genres + Auto-write hooks + Auto-cover in one go"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {polishing
                        ? 'Polishing…'
                        : `AI Polish All${(untaggedCount + missingHooksCount + missingCoversCount) > 0 ? ` (${untaggedCount + missingHooksCount + missingCoversCount})` : ''}`}
                    </Button>
                    <Button
                      onClick={handleAutoTagGenres}
                      disabled={autoTagging}
                      variant="outline"
                      className="border-amber-600 text-amber-300 hover:bg-amber-600/20"
                      data-testid="admin-auto-tag-genres-btn"
                      title="Use AI to fill in missing genres on older content"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {autoTagging ? 'Tagging…' : `Auto-tag genres${untaggedCount > 0 ? ` (${untaggedCount})` : ''}`}
                    </Button>
                    <Button
                      onClick={handleGenerateHooks}
                      disabled={generatingHooks}
                      variant="outline"
                      className="border-cyan-600 text-cyan-300 hover:bg-cyan-600/20"
                      data-testid="admin-generate-hooks-btn"
                      title="Generate a 2-sentence hook and reading level for older content"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {generatingHooks ? 'Writing…' : `Auto-write hooks${missingHooksCount > 0 ? ` (${missingHooksCount})` : ''}`}
                    </Button>
                    <Button
                      onClick={handleAutoCover}
                      disabled={autoCovering}
                      variant="outline"
                      className="border-emerald-600 text-emerald-300 hover:bg-emerald-600/20"
                      data-testid="admin-auto-cover-btn"
                      title="Generate a gradient placeholder cover for any content missing one"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {autoCovering ? 'Painting…' : `Auto-cover${missingCoversCount > 0 ? ` (${missingCoversCount})` : ''}`}
                    </Button>
                    <Button
                      onClick={() => setHealthPanelOpen(true)}
                      variant="outline"
                      className="border-rose-500 text-rose-300 hover:bg-rose-500/20"
                      data-testid="admin-library-health-btn"
                      title="Diagnose & repair broken file pointers, missing covers, and unextracted ebook text"
                    >
                      <Stethoscope className="w-4 h-4 mr-2" />
                      Library Health
                    </Button>
                    <Button
                      onClick={() => navigate('/manage-content')}
                      variant="outline"
                      className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                      data-testid="admin-manage-series-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Manage Series
                    </Button>
                    <Button
                      onClick={() =>
                        contentSelectMode ? exitContentSelectMode() : setContentSelectMode(true)
                      }
                      variant="outline"
                      className={
                        contentSelectMode
                          ? 'bg-rose-600 hover:bg-rose-500 text-white border-rose-500'
                          : 'border-rose-500 text-rose-300 hover:bg-rose-500/20'
                      }
                      data-testid="admin-content-bulk-toggle"
                      title="Toggle multi-select to bulk-delete content"
                    >
                      {contentSelectMode ? (
                        <X className="w-4 h-4 mr-2" />
                      ) : (
                        <CheckSquare className="w-4 h-4 mr-2" />
                      )}
                      {contentSelectMode ? 'Cancel select' : 'Multi-select'}
                    </Button>
                    <Button
                      onClick={() => navigate('/upload')}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="admin-add-content-btn"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Add Content
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <LibraryHealth />
                </div>

                {/* Search bar — also drives "Select all visible" so admin can
                    narrow down then nuke a category in two taps. */}
                <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-slate-900/40 border border-slate-700 rounded-lg">
                  <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <input
                    type="search"
                    value={contentSearch}
                    onChange={(e) => setContentSearch(e.target.value)}
                    placeholder="Search by title, author, or type…"
                    className="flex-1 bg-transparent text-white text-sm placeholder-slate-500 focus:outline-none"
                    data-testid="admin-content-search"
                  />
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold tabular-nums">
                    {visibleContent.length}/{content.length}
                  </span>
                </div>

                {/* Bulk-action bar — only renders in multi-select mode. */}
                {contentSelectMode && (
                  <div
                    className="mb-3 p-3 bg-rose-900/20 border border-rose-500/40 rounded-lg flex flex-wrap items-center gap-2 justify-between"
                    data-testid="admin-content-bulk-bar"
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <button
                        type="button"
                        onClick={() =>
                          allContentSelected
                            ? setContentSelectedIds(new Set())
                            : setContentSelectedIds(new Set(visibleContent.map((c) => c.book_id)))
                        }
                        className="inline-flex items-center gap-1.5 text-rose-200 hover:text-white"
                        data-testid="admin-content-bulk-select-all"
                      >
                        {allContentSelected ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        <span>{allContentSelected ? 'Deselect all' : 'Select all visible'}</span>
                      </button>
                      <span className="text-slate-500">·</span>
                      <span className="text-white font-bold tabular-nums">
                        {contentSelectedIds.size} selected
                      </span>
                    </div>
                    <Button
                      onClick={runContentBulkDelete}
                      disabled={!contentSelectedIds.size || contentBulkBusy}
                      size="sm"
                      className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-bold"
                      data-testid="admin-content-bulk-delete"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      {contentBulkBusy
                        ? 'Deleting…'
                        : `Delete ${contentSelectedIds.size || ''}`.trim()}
                    </Button>
                  </div>
                )}

                <div className="space-y-3">
                  {visibleContent.length === 0 && (
                    <p
                      className="text-center text-slate-500 py-6 text-sm"
                      data-testid="admin-content-empty"
                    >
                      {contentSearch
                        ? `No items match "${contentSearch}".`
                        : 'No content yet.'}
                    </p>
                  )}
                  {visibleContent.map((item) => {
                    const isSelected = contentSelectedIds.has(item.book_id);
                    return (
                      <div
                        key={item.book_id}
                        onClick={
                          contentSelectMode ? () => toggleContentSelected(item.book_id) : undefined
                        }
                        className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                          contentSelectMode ? 'cursor-pointer ' : ''
                        }${
                          isSelected
                            ? 'bg-rose-600/30 ring-2 ring-rose-400/60'
                            : 'bg-slate-700/30 hover:bg-slate-700/50'
                        }`}
                        data-testid={`admin-content-row-${item.book_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {contentSelectMode && (
                            <span className="flex-shrink-0">
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-rose-300" />
                              ) : (
                                <Square className="w-5 h-5 text-slate-400" />
                              )}
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate">{item.title}</p>
                            <p className="text-sm text-gray-400 truncate">
                              {item.author} • {item.type}
                            </p>
                          </div>
                        </div>
                        {!contentSelectMode && (
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-white border-slate-600"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-400 border-red-600"
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Writers Tab */}
          <TabsContent value="writers">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-2">
                <CardTitle className="text-white flex items-center gap-2">
                  Scribe Applications
                  {pendingWritersCount > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      {pendingWritersCount} pending
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {pendingWriters.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={selectAllSpamRisk}
                      disabled={scanningSpam || bulkBusy}
                      className="text-amber-300 border-amber-500/40 hover:bg-amber-600/10 h-7 px-2 text-xs"
                      data-testid="select-spam-risk-btn"
                      title="Auto-select every brand-new account with zero engagement"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {scanningSpam ? 'Scanning...' : 'Select spam-risk'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchPendingWriters}
                    className="text-gray-300 border-slate-600 hover:bg-slate-700/50 h-7 px-2 text-xs"
                    data-testid="refresh-pending-writers-btn"
                  >
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pendingWriters.length === 0 ? (
                  <p className="text-gray-400 text-center py-8" data-testid="no-pending-writers">
                    No pending writer applications.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Bulk action bar */}
                    <div className="flex items-center gap-3 px-2 pb-2 border-b border-slate-700/50 flex-wrap">
                      <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={
                            pendingWriters.length > 0 &&
                            pendingWriters.every((w) => selectedWriterIds.has(w.user_id))
                          }
                          onChange={toggleSelectAllWriters}
                          className="w-4 h-4 accent-fuchsia-500"
                          data-testid="select-all-writers-checkbox"
                        />
                        Select all
                      </label>
                      {selectedWriterIds.size > 0 && (
                        <>
                          <span className="text-xs text-fuchsia-300 font-semibold" data-testid="selected-writers-count">
                            {selectedWriterIds.size} selected
                          </span>
                          <div className="flex gap-1.5 ml-auto">
                            <Button
                              size="sm"
                              disabled={bulkBusy}
                              onClick={() => bulkDecide('approve')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs"
                              data-testid="bulk-approve-writers-btn"
                            >
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve {selectedWriterIds.size}
                            </Button>
                            <Button
                              size="sm"
                              disabled={bulkBusy}
                              variant="outline"
                              onClick={() => bulkDecide('reject')}
                              className="text-red-400 border-red-600/50 hover:bg-red-600/10 h-7 px-3 text-xs"
                              data-testid="bulk-reject-writers-btn"
                            >
                              Reject {selectedWriterIds.size}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedWriterIds(new Set())}
                              disabled={bulkBusy}
                              className="text-gray-400 border-slate-600 hover:bg-slate-700/50 h-7 px-2 text-xs"
                              data-testid="clear-writers-selection-btn"
                            >
                              Clear
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    {pendingWriters.map((w) => {
                      const ts = w.writer_application_at || w.created_at;
                      const ago = ts ? new Date(ts).toLocaleString() : '';
                      const isBusy = decidingWriterId === w.user_id;
                      const isExpanded = expandedWriterId === w.user_id;
                      const activity = activityCache[w.user_id];
                      const isLoadingAct = loadingActivityFor === w.user_id;
                      return (
                        <div
                          key={w.user_id}
                          className="bg-slate-700/30 rounded-lg border border-slate-700/50 overflow-hidden"
                          data-testid={`pending-writer-row-${w.user_id}`}
                        >
                          <div className="flex items-center gap-3 p-3">
                            <input
                              type="checkbox"
                              checked={selectedWriterIds.has(w.user_id)}
                              onChange={() => toggleSelectWriter(w.user_id)}
                              className="w-4 h-4 accent-fuchsia-500 flex-shrink-0"
                              data-testid={`select-writer-checkbox-${w.user_id}`}
                              aria-label={`Select ${w.name || w.email}`}
                            />
                            {w.picture ? (
                              <img src={w.picture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-purple-600/30 text-purple-200 font-bold flex items-center justify-center flex-shrink-0">
                                {(w.name || w.email || '?')[0].toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate" data-testid={`pending-writer-name-${w.user_id}`}>{w.name || w.email}</p>
                              <p className="text-xs text-gray-400 truncate">
                                {w.email}
                                {w.auth_provider === 'google' && (
                                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">
                                    Google
                                  </span>
                                )}
                              </p>
                              {ago && <p className="text-[10px] text-gray-500 mt-0.5">Applied: {ago}</p>}
                            </div>
                            <div className="flex gap-2 flex-shrink-0 items-center">
                              <button
                                type="button"
                                onClick={() => toggleWriterActivity(w.user_id)}
                                className="text-gray-400 hover:text-white transition-colors text-xs px-2 py-1.5 rounded border border-slate-600 hover:border-slate-400 flex items-center gap-1"
                                data-testid={`view-writer-activity-${w.user_id}`}
                                aria-expanded={isExpanded}
                              >
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                <span className="hidden sm:inline">{isExpanded ? 'Hide' : 'Activity'}</span>
                              </button>
                              <Button
                                size="sm"
                                disabled={isBusy}
                                onClick={() => decideWriter(w.user_id, 'approve', w.name || w.email)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid={`approve-writer-${w.user_id}`}
                              >
                                <Check className="w-3.5 h-3.5 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                disabled={isBusy}
                                variant="outline"
                                onClick={() => decideWriter(w.user_id, 'reject', w.name || w.email)}
                                className="text-red-400 border-red-600/50 hover:bg-red-600/10"
                                data-testid={`reject-writer-${w.user_id}`}
                              >
                                Reject
                              </Button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div
                              className="border-t border-slate-700/60 px-4 py-3 bg-slate-900/40"
                              data-testid={`writer-activity-panel-${w.user_id}`}
                            >
                              {isLoadingAct && !activity ? (
                                <p className="text-xs text-gray-400 py-2 text-center">Loading activity profile...</p>
                              ) : !activity ? (
                                <p className="text-xs text-gray-500 py-2 text-center">No activity data available.</p>
                              ) : (
                                <div className="space-y-3">
                                  {activity.spam_risk && (
                                    <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30">
                                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                                      <p className="text-xs text-amber-200">
                                        <strong>Possible spam:</strong> brand-new account, no books opened, no engagement.
                                        Consider rejecting unless they have credentials.
                                      </p>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <div className="bg-slate-800/60 rounded-md px-3 py-2">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Account age</p>
                                      <p className="text-base font-bold text-white">{activity.account_age_days}d</p>
                                    </div>
                                    <div className="bg-slate-800/60 rounded-md px-3 py-2">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Books opened</p>
                                      <p className="text-base font-bold text-white">{activity.books_opened}</p>
                                      {activity.books_completed > 0 && (
                                        <p className="text-[10px] text-emerald-400">{activity.books_completed} completed</p>
                                      )}
                                    </div>
                                    <div className="bg-slate-800/60 rounded-md px-3 py-2">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Coins</p>
                                      <p className="text-base font-bold text-amber-300">{activity.coins}</p>
                                    </div>
                                    <div className="bg-slate-800/60 rounded-md px-3 py-2">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500">Streak</p>
                                      <p className="text-base font-bold text-white">{activity.streak_current}d</p>
                                      {activity.streak_best > activity.streak_current && (
                                        <p className="text-[10px] text-gray-400">best {activity.streak_best}d</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-3 text-xs">
                                    <div className="flex-1">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Top genres</p>
                                      {activity.top_genres?.length ? (
                                        <div className="flex flex-wrap gap-1">
                                          {activity.top_genres.map((tg) => (
                                            <span
                                              key={tg.genre}
                                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-200 border border-purple-500/30"
                                            >
                                              {tg.genre}
                                              <span className="text-[10px] text-purple-300/70">×{tg.count}</span>
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-gray-500">— never opened a book —</p>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Last activity</p>
                                      <p className="text-gray-300">
                                        {activity.last_activity_at
                                          ? new Date(activity.last_activity_at).toLocaleString()
                                          : '—'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <AdminPaymentsTab />
          </TabsContent>

          {/* Coin Central Bank — admin-only treasury, audited ledger of
              every spent coin that doesn't flow to another user. */}
          <TabsContent value="central-bank">
            <CoinCentralBank canDisburse={!isCoadmin} />
            {/* Ambassador slot manager mounted underneath because it
                shares the same admin treasury context (#001 = main
                admin · co-admin spots 002–005). */}
            {!isCoadmin && <div className="mt-6"><AmbassadorSlotsPanel /></div>}
            {/* Feb 12 2026 — High King-only link/unlink UI so account
                pairs can share their displayed XP + coin totals while
                keeping their own roles + sessions. */}
            {!isCoadmin && <div className="mt-6"><AccountMergeCard /></div>}
          </TabsContent>

          {/* Shop Analytics Tab */}
          <TabsContent value="shop">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-300" />
                  Coin Shop Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ShopAnalytics />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <CoverAudit />
          </TabsContent>

          <TabsContent value="vault">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lock className="w-4 h-4 text-fuchsia-300" />
                  Secret Vault
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AdminVaultManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="experiments">
            <AdminExperiments />
          </TabsContent>

          {/* Aggregate Analytics tab — surfaces every analytics surface
              the dashboard already has (Onboarding + Shop) in one place,
              plus quick top-line counts. Deep-linkable via
              navigate('/admin', { state: { tab: 'analytics' } }) from the
              TopBar's quick-shortcut button. Feb 26 2026. */}
          <TabsContent value="analytics">
            <Card className="bg-slate-800/50 border-slate-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                  Platform Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-slate-900/60 border border-slate-700/60 p-4 text-center" data-testid="admin-analytics-users">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Users</p>
                    <p className="text-3xl font-black text-cyan-400 tabular-nums mt-1">{users.length}</p>
                  </div>
                  <div className="rounded-xl bg-slate-900/60 border border-slate-700/60 p-4 text-center" data-testid="admin-analytics-writers">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Scribes</p>
                    <p className="text-3xl font-black text-purple-400 tabular-nums mt-1">{writers.length}</p>
                  </div>
                  <div className="rounded-xl bg-slate-900/60 border border-slate-700/60 p-4 text-center" data-testid="admin-analytics-content">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Content</p>
                    <p className="text-3xl font-black text-emerald-400 tabular-nums mt-1">{content.length}</p>
                  </div>
                  <div className="rounded-xl bg-slate-900/60 border border-slate-700/60 p-4 text-center" data-testid="admin-analytics-pending">
                    <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Pending</p>
                    <p className="text-3xl font-black text-amber-400 tabular-nums mt-1">{pendingContent.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mb-6">
              <OnboardingAnalytics />
            </div>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-400" />
                  Coin Shop Analytics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ShopAnalytics />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages">
            <AdminMessagesTab />
          </TabsContent>

          <TabsContent value="quests">
            <AdminQuestsTab />
          </TabsContent>

          <TabsContent value="contracts">
            <AdminContractsTab />
          </TabsContent>

          <TabsContent value="top-series">
            <AdminTopSeriesTab />
          </TabsContent>

          <TabsContent value="reports">
            <AdminContentReportsTab role={viewerRole} />
          </TabsContent>

          <TabsContent value="player-ads">
            <AdminPlayerAdsTab role={viewerRole} />
          </TabsContent>

          <TabsContent value="book-club">
            <AdminBookClubTab role={viewerRole} />
          </TabsContent>

          <TabsContent value="trash">
            <AdminTrashTab />
          </TabsContent>

          <TabsContent value="sub-transfers">
            <AdminSubscriptionTransfersTab />
          </TabsContent>
        </Tabs>

        {/* Quick Stats Overview — Jun 3 2026 v2: each tile opens its own
            dedicated breakdown page (`/admin/breakdown/:section`) with the
            full list and search/sort. Replaces the previous "switch tab"
            behaviour so the breakdown view stays focused. */}
        <AdminQuickStats
          users={users}
          content={content}
          pendingContent={pendingContent}
          navigate={navigate}
        />
      </main>

      {/* Password Reset Modal */}
      <TempPasswordModal
        resetPasswordUser={resetPasswordUser}
        tempPassword={tempPassword}
        passwordCopied={passwordCopied}
        onCopy={copyPassword}
        onClose={closePasswordModal}
      />

      {/* Countdown delete confirmation for users & scribes */}
      <ConfirmDeleteDialog
        open={!!pendingDeleteUser}
        title={
          pendingDeleteUser
            ? `Delete ${pendingDeleteUser.kind}: ${pendingDeleteUser.name || pendingDeleteUser.email}`
            : 'Delete'
        }
        description={
          pendingDeleteUser?.kind === 'writer'
            ? `This permanently deletes writer account "${pendingDeleteUser?.email}". Their uploaded content stays in the library.`
            : `This permanently deletes user "${pendingDeleteUser?.email}" and all their progress, coins, perks, quiz history, and uploaded content. This cannot be undone.`
        }
        confirmLabel={`Delete ${pendingDeleteUser?.kind || 'user'}`}
        countdownSeconds={5}
        onCancel={() => setPendingDeleteUser(null)}
        onConfirm={confirmDeleteUserOrWriter}
        testId="admin-confirm-delete"
      />

      {/* Countdown confirmation for revoking Secret Vault access */}
      <ConfirmDeleteDialog
        open={!!pendingRevokeVault}
        title={
          pendingRevokeVault
            ? `Revoke Vault access: ${pendingRevokeVault.name}`
            : 'Revoke Vault'
        }
        description={
          pendingRevokeVault
            ? `This revokes Secret Vault access for "${pendingRevokeVault.email || pendingRevokeVault.name}". After confirming, you will have a 10-second undo window before the change actually takes effect — plenty of time to take it back if you change your mind.`
            : ''
        }
        confirmLabel="Revoke Vault"
        cancelLabel="Keep access"
        countdownSeconds={3}
        onCancel={() => setPendingRevokeVault(null)}
        onConfirm={confirmRevokeVault}
        testId="admin-confirm-revoke-vault"
      />

      {/* Grant Vault modal */}
      {grantVaultUser && (
        <GrantVaultModal
          user={grantVaultUser}
          onClose={() => setGrantVaultUser(null)}
          onGranted={fetchData}
        />
      )}

      {/* Grant Premium modal — row-level admin action. Mirrors the
          drawer's premium-grant form so admins can extend tiers without
          opening a user profile first. */}
      {grantPremiumUser && (
        <GrantPremiumModal
          user={grantPremiumUser}
          onClose={() => setGrantPremiumUser(null)}
          onGranted={fetchData}
        />
      )}

      {/* Gift Streak modal — admins only. */}
      <GiftStreakModal
        giftStreakTarget={giftStreakTarget}
        giftStreakDays={giftStreakDays}
        giftStreakReason={giftStreakReason}
        giftStreakBusy={giftStreakBusy}
        setGiftStreakDays={setGiftStreakDays}
        setGiftStreakReason={setGiftStreakReason}
        onClose={closeGiftStreakModal}
        onSubmit={submitGiftStreak}
      />

      {/* User profile drawer — opens from any clickable user row. */}
      {profileUserId && (
        <UserProfileDrawer
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}

      {/* Activity analytics dialog (1/7/30 day windows) — Feb 2026 */}
      {activityUserId && (
        <AdminUserActivityDialog
          userId={activityUserId}
          onClose={() => setActivityUserId(null)}
        />
      )}

      {/* Library Health diagnostic + repair panel */}
      <LibraryHealthPanel
        open={healthPanelOpen}
        onClose={() => setHealthPanelOpen(false)}
      />
    </>
  );
};

export default AdminDashboard;
